import React, { createContext, useContext, useState, ReactNode, useCallback } from 'react'

const CONTROLLER_SCALE_KEY = 'settings.kioskControllerUiScale'
const DISPLAY_SCALE_KEY = 'settings.kioskDisplayUiScale'
const FORCE_VK_KEY = 'settings.forceVirtualKeyboard'

function readScale(key: string, fallback: number): number {
  if (typeof window === 'undefined') return fallback
  const raw = window.localStorage.getItem(key)
  if (raw == null || raw === '') return fallback
  const n = Number(raw)
  if (!Number.isFinite(n)) return fallback
  return Math.min(1.75, Math.max(0.5, n))
}

interface SettingsContextType {
  aiAssistant: boolean
  showAppDescriptions: boolean
  controllerUiScale: number
  displayUiScale: number
  /** Desktop testing: force the kiosk on-screen keyboard. */
  forceVirtualKeyboard: boolean
  /** Desktop testing: show lobby sleep / attract fullscreen. */
  sleepPreviewOpen: boolean
  toggleAIAssistant: () => void
  toggleShowAppDescriptions: () => void
  toggleForceVirtualKeyboard: () => void
  openSleepPreview: () => void
  closeSleepPreview: () => void
  setControllerUiScale: (scale: number) => void
  setDisplayUiScale: (scale: number) => void
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined)

export const useSettings = () => {
  const context = useContext(SettingsContext)
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider')
  }
  return context
}

interface SettingsProviderProps {
  children: ReactNode
}

export const SettingsProvider: React.FC<SettingsProviderProps> = ({ children }) => {
  const [aiAssistant, setAIAssistant] = useState(false)
  const [showAppDescriptions, setShowAppDescriptions] = useState(() => {
    if (typeof window === 'undefined') return true
    const raw = window.localStorage.getItem('settings.showAppDescriptions')
    if (raw === '0') return false
    if (raw === '1') return true
    return true
  })
  const [forceVirtualKeyboard, setForceVirtualKeyboard] = useState(() => {
    if (typeof window === 'undefined') return false
    return window.localStorage.getItem(FORCE_VK_KEY) === '1'
  })
  const [sleepPreviewOpen, setSleepPreviewOpen] = useState(false)
  const [controllerUiScale, setControllerUiScaleState] = useState(() => readScale(CONTROLLER_SCALE_KEY, 1))
  const [displayUiScale, setDisplayUiScaleState] = useState(() => readScale(DISPLAY_SCALE_KEY, 1))

  const toggleAIAssistant = () => {
    setAIAssistant(prev => !prev)
  }

  const toggleShowAppDescriptions = () => {
    setShowAppDescriptions(prev => {
      const next = !prev
      if (typeof window !== 'undefined') {
        window.localStorage.setItem('settings.showAppDescriptions', next ? '1' : '0')
      }
      return next
    })
  }

  const toggleForceVirtualKeyboard = useCallback(() => {
    setForceVirtualKeyboard((prev) => {
      const next = !prev
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(FORCE_VK_KEY, next ? '1' : '0')
      }
      return next
    })
  }, [])

  const openSleepPreview = useCallback(() => setSleepPreviewOpen(true), [])
  const closeSleepPreview = useCallback(() => setSleepPreviewOpen(false), [])

  const setControllerUiScale = useCallback((scale: number) => {
    const next = Math.min(1.75, Math.max(0.5, Number(scale) || 1))
    setControllerUiScaleState(next)
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(CONTROLLER_SCALE_KEY, String(next))
    }
  }, [])

  const setDisplayUiScale = useCallback((scale: number) => {
    const next = Math.min(1.75, Math.max(0.5, Number(scale) || 1))
    setDisplayUiScaleState(next)
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(DISPLAY_SCALE_KEY, String(next))
    }
  }, [])

  const value = {
    aiAssistant,
    showAppDescriptions,
    controllerUiScale,
    displayUiScale,
    forceVirtualKeyboard,
    sleepPreviewOpen,
    toggleAIAssistant,
    toggleShowAppDescriptions,
    toggleForceVirtualKeyboard,
    openSleepPreview,
    closeSleepPreview,
    setControllerUiScale,
    setDisplayUiScale,
  }

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  )
}
